import { lazy, Suspense, useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import Slider from 'react-slick'
import Img from 'react-image'

import './styles.scss'
import soldSvg from './sold-text.svg'
import EmbeddedVideo from '../../../CarsSearch/components/EmbeddedVideo'

const Lightbox = lazy(() => import('./Lightbox'))

function CarImagePreview() {
  const [state, setState] = useState({
    nav1: null,
    nav2: null,
    currentSlide: 0,
    lightboxToggler: false,
    lightboxSlide: 1,
    showCarousel: false,
  })

  const [loadedMedias, setLoadedMedias] = useState(App.State.images)
  const slider1 = useRef(null)
  const slider2 = useRef(null)

  const medias = loadedMedias.map(media => {
    if (media.type === 'image') {
      return {
        url: media.image ? (App.State.supportsWebp && media.image.webp ? media.image.webp.large.url : media.image.default.large.url) : '',
        type: 'image'
      };
    } else if (media.type === 'video') {
      return {
        url: `https://www.youtube.com/embed/${media.video_url}`,
        type: 'video'
      };
    }
  })

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      nav1: slider1.current,
      nav2: slider2.current,
    }))
  }, [])

  const fireSlideInteractionEvent = () => {
    App.GAEvent('Car Page', 'Click Main Image', null, null)
  }

  const handleLightboxSlide = (slide) => {
    setState(prevState => ({
      ...prevState,
      lightboxToggler: !prevState.lightboxToggler,
      lightboxSlide: slide + 1,
    }))

    fireSlideInteractionEvent()
  }

  const handleLightboxUpdate = (instance) => {
    const slideIndex = instance.state.slide
    const totalSlides = loadedMedias.length

    if (slideIndex > 0 && slideIndex <= totalSlides) {
      slider1.current.slickGoTo(slideIndex - 1)
      setState((prevState) => ({ ...prevState, lightboxSlide: slideIndex }))
    }
  }

  const handleSlide = (next) => {
    setState(prevState => ({ ...prevState, currentSlide: next }))

    fireSlideInteractionEvent()
  }

  const mainSliderSettings = {
    swipeToSlide: true,
    infinite: true,
    focusOnSelect: true,
    speed: 300,
    lazyLoad: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: handleSlide,
  }

  const thumbSliderSettings = {
    swipeToSlide: true,
    focusOnSelect: true,
    speed: 250,
    infinite: true,
    lazyLoad: true,
    slidesToShow: Math.min(loadedMedias.length, 6),
    slidesToScroll: 6,
    afterChange: handleSlide,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: Math.min(loadedMedias.length, 4),
          slidesToScroll: 4,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: Math.min(loadedMedias.length, 3),
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: Math.min(loadedMedias.length, 3),
          slidesToScroll: 1,
        },
      },
    ],
  }

  const handleImageClick = () => {
    App.Request.post(`/cars/medias`, {
      id: App.State.carId,
    }).then(({ data }) => {
      setLoadedMedias(data)
      setState((prevState) => ({
        ...prevState,
        showCarousel: true,
      }))
    })
  }

  const getOnClickHandler = () => {
    if (state.showCarousel) {
      return () => handleLightboxSlide(state.currentSlide)
    } else {
      return handleImageClick
    }
  }

  return (
    <>
      <div className="main-slider p-0">
        <Slider
          asNavFor={state.nav2}
          ref={(slider) => (slider1.current = slider)}
          {...mainSliderSettings}
          accessibility={!state.lightboxToggler}
        >
          {loadedMedias.map((media, index) => {
            if (media.type === 'image') {
              return (
                <div
                  style={{ cursor: 'pointer' }}
                  key={`large-image-${media.id}`}
                >
                  <div className="main-img-container fixed-height">
                    {App.State.sold && (
                      <div
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          zIndex: 100,
                          opacity: '0.3',
                          backgroundSize: '100% 100%',
                          backgroundImage: `url(${soldSvg})`,
                          pointerEvents: App.State.preserveAfterSold
                            ? 'none'
                            : 'auto',
                        }}
                      />
                    )}
                    <Img
                      onClick={ getOnClickHandler()}
                      src={
                        App.State.supportsWebp &&
                        Object.prototype.hasOwnProperty.call(
                          media.image,
                          'webp'
                        )
                          ? media.image.webp.medium.url
                          : media.image.default.medium.url
                      }
                      alt={media.alt_title}
                      className="flexible-img"
                      style={{ width: '100%' }}
                      loader={
                        <img
                          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8+ertfwAJRQPBTZk8XgAAAABJRU5ErkJggg=="
                          width={730}
                          height={500}
                          alt={media.alt_title}
                        />
                      }
                    />
                    {loadedMedias.length > 1 && (
                      <div className="image-count shadow">
                        <i className="far fa-images align-middle count-icon image-count-content me-1" />
                        <p className="image-count-text image-count-content">
                          {index + 1}/{loadedMedias.length}
                        </p>
                      </div>
                    )}
                    <div className="image-zoom shadow">
                      <i className="far fa-search image-zoom-content" />
                    </div>
                  </div>
                </div>
              )
            } else if (media.type === 'video') {
              return (
                <EmbeddedVideo videoUrl={media.video_url} key={`video-${media.id}`} />
              )
            }
          })}
        </Slider>
      </div>
      <div className="item-image-list">
        {!state.showCarousel ? (
          <div>
            <button
              className="btn btn-primary mt-3"
              onClick={handleImageClick}
            >
              Click for image/video gallery
            </button>
          </div>
        ) : (
          <Slider
            asNavFor={state.nav1}
            ref={(slider) => (slider2.current = slider)}
            {...thumbSliderSettings}
            accessibility={!state.lightboxToggler}
          >
            {loadedMedias.map((media) => {
              if (media.type === 'image') {
                return (
                  <Img
                  src={
                  App.State.supportsWebp &&
                  Object.prototype.hasOwnProperty.call(media.image, 'webp')
                  ? media.image.webp.thumb.url
                  : media.image.default.thumb.url
                }
                alt={media.alt_title}
                key={`thumb-image-${media.id}`}
                height={75}
                loader={
                  <img
                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8+ertfwAJRQPBTZk8XgAAAABJRU5ErkJggg=="
                    width={100}
                    height={75}
                    alt={media.alt_title}
                  />
                }
              />
              )
            } else if (media.type === 'video') {
                return (
                  <div className="video-thumbnail">
                    <Img
                      src={
                        media.thumbnail
                      }
                      alt={media.thumbnail}
                      key={`thumb-image-${media.id}`}
                      height={75}
                      loader={
                        <img
                          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8+ertfwAJRQPBTZk8XgAAAABJRU5ErkJggg=="
                          width={100}
                          height={75}
                          alt={media.thumbnail}
                        />
                      }
                    />
                    <i className="fa-brands fa-youtube fa-xl"></i>
                  </div>
                )
              }
            })}
          </Slider>
        )}
      </div>
      <Suspense fallback={<div>Loading...</div>}>
        <Lightbox
          slide={state.lightboxSlide}
          toggler={state.lightboxToggler}
          images={medias}
          onSlideChange={handleLightboxUpdate}
        />
      </Suspense>
    </>
  )
}

document.addEventListener('DOMContentLoaded', () => {
  ReactDOM.render(
    <CarImagePreview />,
    document.getElementById('car-image-preview')
  )
})
